/**
 * Das Anlege-Menü des Kalenders (Spec 45, Regel 10, R5/R6): zwei Einträge —
 * „Termin" (`duedate-add`) und „Aufgabe" (`task-add`). Eine Bauform für zwei
 * Einstiege: der Kopf (`kal-add-menu`, Akzentfläche „Anlegen ▾", Vorbelegung
 * `range.today`) und das Plus in der Tageszelle (`kal-tag-add-<datum>`,
 * Vorbelegung der Tag der Zelle) — kein Termin-only-Sonderweg mehr.
 *
 * Die Beschriftung ist „Anlegen", nicht „+ Neu" (`design-system`: aktives
 * Verb). Das Menü ist `PopoverMenu` (`rail-konto-menu`-Bauform): Pfeile/Enter,
 * `Esc` und Klick daneben schließen.
 */

import { ChevronDown, Plus } from 'lucide-react'

import { PopoverMenu } from '@/components/PopoverMenu'

const ITEM_CLASS =
  'text-body text-text hover:bg-bg focus-visible:bg-bg flex min-h-(--tap-min) w-full items-center px-3 py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)'

interface CalendarAddMenuProps {
  readonly variant: 'header' | 'cell'
  readonly triggerTestId: string
  readonly menuTestId: string
  readonly triggerLabel: string
  readonly onAddAppointment: () => void
  readonly onAddTask: () => void
}

export function CalendarAddMenu({
  variant,
  triggerTestId,
  menuTestId,
  triggerLabel,
  onAddAppointment,
  onAddTask,
}: CalendarAddMenuProps) {
  const isHeader = variant === 'header'

  return (
    <PopoverMenu
      triggerTestId={triggerTestId}
      triggerLabel={triggerLabel}
      triggerClassName={
        isHeader
          ? 'bg-accent rounded-card text-body flex min-h-(--tap-min) items-center gap-1 px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)'
          : // Beim Überfahren sichtbar (`design-system`) — der Wrapper blendet ein,
            // Tastaturfokus und ein offenes Menü halten ihn sichtbar. `size-6`
            // (24px, WCAG 2.2 2.5.8) wie die Trefferfläche eines Eintrags.
            'text-muted hover:text-accent focus-visible:text-accent flex size-6 items-center justify-center'
      }
      wrapperClassName={
        isHeader
          ? 'ml-1'
          : 'absolute top-1 right-1 opacity-0 transition-opacity duration-(--duration-fast) ease-(--ease-out) group-focus-within:opacity-100 group-hover:opacity-100 has-[[aria-expanded=true]]:opacity-100'
      }
      menuTestId={menuTestId}
      menuClassName={`border-border bg-surface rounded-card absolute z-50 w-44 border ${
        isHeader ? 'top-full right-0 mt-1' : 'top-full right-0'
      }`}
      trigger={
        isHeader ? (
          <>
            Anlegen
            <ChevronDown aria-hidden className="size-4" />
          </>
        ) : (
          <Plus aria-hidden className="size-3.5" />
        )
      }
    >
      {(close) => (
        <>
          <button
            type="button"
            role="menuitem"
            data-testid="duedate-add"
            onClick={() => {
              close()
              onAddAppointment()
            }}
            className={ITEM_CLASS}
          >
            Termin
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="task-add"
            onClick={() => {
              close()
              onAddTask()
            }}
            className={ITEM_CLASS}
          >
            Aufgabe
          </button>
        </>
      )}
    </PopoverMenu>
  )
}
